import React, { useEffect, useMemo, useState } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import { formatDuration } from '../../utils/timeUtils';
import styles from './ProductInfoChips.module.css';
import { FiUser, FiUsers, FiMusic, FiBarChart2, FiBook, FiFile, FiClock } from 'react-icons/fi';

const ProductInfoChips = ({ item, intervalMs = 4000, ensemble = false, parts = false, performers = false, difficulty = false, level = false, numberofpages = false, duration = false }) => {
  const isMobile = useMediaQuery('(max-width: 800px)');
  const [pairIndex, setPairIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  const truncateText = (text, maxLength=16) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const infoChips = useMemo(() => {
    if (!item) return [];

    const chips = [];

    if (item.ensemble && ensemble) {
      chips.push(
        <div key="ensemble" className={styles.item}>
          <FiMusic aria-label="Ensemble" title="Ensemble" className={styles.icon} />
          <span>{isMobile ? truncateText(item.ensemble) : item.ensemble}</span>
        </div>
      );
    }

    if (item.parts && parts) {
      chips.push(
        <div key="parts" className={styles.item}>
          <FiUsers aria-label="Parts" title="Parts" className={styles.icon} />
          <span>{isMobile ? truncateText(item.parts) : item.parts}</span>
        </div>
      );
    }

    // Recommended Number of performers (if provided separately)
    if (item.recommendednumber && performers) {
      const count = item.recommendednumber;
      const label = count === 1 ? 'performer' : 'performers';
      chips.push(
        <div key="performers" className={styles.item}>
          <FiUser aria-label="Performers" title="Performers" className={styles.icon} />
          <span>{count} {label}</span>
        </div>
      );
    }

    if (item.difficulty && difficulty) {
      chips.push(
        <div key="difficulty" className={styles.item}>
          <FiBarChart2 aria-label="Difficulty" title="Difficulty" className={styles.icon} />
          <span>{isMobile ? truncateText(item.difficulty) : item.difficulty}</span>
        </div>
      );
    }

    if (item.level && level) {
      chips.push(
        <div key="level" className={styles.item}>
          <FiBook aria-label="Level" title="Level" className={styles.icon} />
          <span>{isMobile ? truncateText(item.level) : item.level}</span>
        </div>
      );
    }

    if (item.numberofpages && numberofpages) {
      chips.push(
        <div key="pages" className={styles.item}>
          <FiFile aria-label="Pages" title="Pages" className={styles.icon} />
          <span>{item.numberofpages} {item.numberofpages > 1 ? 'pages' : 'page'}</span>
        </div>
      );
    }

    if (item.duration && duration) {
      chips.push(
        <div key="duration" className={styles.item}>
          <FiClock aria-label="Duration" title="Duration" className={styles.icon} />
          <span>{formatDuration(item.duration)}</span>
        </div>
      );
    }

    return chips;
  }, [item, ensemble, parts, performers, difficulty, level, numberofpages, duration, isMobile]);

  const pairs = useMemo(() => {
    const out = [];
    for (let i = 0; i < infoChips.length; i += 2) {
      out.push(infoChips.slice(i, i + 2));
    }
    return out.length ? out : [[]];
  }, [infoChips]);

  useEffect(() => {
    setPairIndex(0);
  }, [isMobile, infoChips.length]);

  useEffect(() => {
    if (!isMobile || pairs.length <= 1) return;
    const id = setInterval(() => {
      setIsFading(true);
      const timeout = setTimeout(() => {
        setPairIndex((i) => (i + 1) % pairs.length);
        setIsFading(false);
      }, Math.min(200, Math.max(100, Math.floor(intervalMs * 0.25))));
      return () => clearTimeout(timeout);
    }, intervalMs);
    return () => clearInterval(id);
  }, [isMobile, pairs.length, intervalMs]);

  return (
    <div className={styles.section}>
      {isMobile ? (
        <div className={`${styles.mobileCarousel} ${isFading ? styles.fadeOut : ''}`}>
          {pairs[pairIndex].map((chip) => chip)}
        </div>
      ) : (
        infoChips
      )}
    </div>
  );
};

export default ProductInfoChips;
